import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TermsAndConditionsModal, { DESIGNER_TERMS_VERSION, FUNERAL_DIRECTOR_TERMS_VERSION } from './TermsAndConditionsModal';
import apiClient from '../lib/api';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isValidatingToken, user, currentAccountType } = useAuth();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [checkingTerms, setCheckingTerms] = useState(true);
  const [termsType, setTermsType] = useState('designer');

  useEffect(() => {
    const checkTermsAcceptance = async () => {
      if (!isAuthenticated || isLoading || isValidatingToken) {
        setCheckingTerms(false);
        return;
      }

      try {
        const profile = await apiClient.getProfile();
        const userData = profile?.user || profile;
        
        // Check for designers
        if (currentAccountType === 'designer' && user?.type === 'designer') {
          const hasAcceptedTerms = userData?.acceptedTerms?.designer?.version === DESIGNER_TERMS_VERSION;
          
          if (!hasAcceptedTerms) {
            setTermsType('designer');
            setShowTermsModal(true);
            setCheckingTerms(false);
            return;
          }
        }
        
        // Check for funeral directors
        if (currentAccountType === 'funeral_director' && user?.type === 'funeral_director') {
          const hasAcceptedTerms = userData?.acceptedTerms?.funeral_director?.version === FUNERAL_DIRECTOR_TERMS_VERSION;
          
          if (!hasAcceptedTerms) {
            setTermsType('funeral_director');
            setShowTermsModal(true);
            setCheckingTerms(false);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking terms acceptance:', error);
        // On error, allow access but log the issue
      }
      
      setCheckingTerms(false);
    };

    checkTermsAcceptance();
  }, [isAuthenticated, isLoading, isValidatingToken, user, currentAccountType]);

  const handleAcceptTerms = async (version) => {
    try {
      await apiClient.acceptTerms(termsType, version);
      // Refresh user data
      const profile = await apiClient.getProfile();
      const userData = profile?.user || profile;
      
      // Update localStorage with new user data
      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      setShowTermsModal(false);
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  };

  // Show loading state while checking authentication or validating token
  if (isLoading || isValidatingToken || checkingTerms) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div>
          {isValidatingToken ? 'Validating session...' : checkingTerms ? 'Checking terms...' : 'Loading...'}
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show terms modal if needed (blocks access until accepted)
  if (showTermsModal) {
    return (
      <>
        <TermsAndConditionsModal
          open={showTermsModal}
          onAccept={handleAcceptTerms}
          user={user}
          termsType={termsType}
        />
        {/* Don't render children until terms are accepted */}
        <div style={{ display: 'none' }}>{children}</div>
      </>
    );
  }

  // Render the protected content
  return children;
};

export default ProtectedRoute;
